// DELIVERY DATE / DATEPICKER-CALENDAR FIELD MIXIN
import moment from 'moment-timezone';
import {en, de, nl} from 'vuejs-datepicker/dist/locale';

/**
 * The 'moment' date format that the datepicker will return when the customer selects a date.
 *
 * @type {string}
 */
export const DATEPICKER_DATE_FORMAT = 'D. MMM YYYY';

/**
 * Use these for a specific delivery time that the customer can't set. If the time dropdown
 * component see this this time, it will display a blank value and force the customer to choose
 * something.
 * @see \mwc\mymy\models\forms\funnel\DeliveryFormModel::DEFAULT_DELIVERY_TIME_HOUR
 * @see \mwc\mymy\models\forms\funnel\DeliveryFormModel::DEFAULT_DELIVERY_TIME_MINUTE
 * @see \mwc\mymy\models\forms\funnel\DeliveryFormModel::DEFAULT_DELIVERY_TIME_SECOND
 */
/** @type {number} */
export const DEFAULT_DELIVERY_TIME_HOUR = 3;
/** @type {number} */
export const DEFAULT_DELIVERY_TIME_MINUTE = 0;
/** @type {number} */
export const DEFAULT_DELIVERY_TIME_SECOND = 0;

/**
 * Get the timezone for the current country the customer is shopping in.
 *
 * @return {string}
 */
export const timezone = () => {
   switch (window.mymy.request.currentSite.country) {
      case 'nl':
         return 'Europe/Amsterdam';
      case 'at':
         return 'Europe/Vienna';
      default:
         return 'Europe/Berlin';
   }
}

/**
 * The earliest delivery date a customer can select for the menu they are viewing or have in
 * their cart.
 *
 * @return {Moment}
 */
export const minDeliveryDate = () => {
   let daysToDisable = window.mymy.menu.minimumPreparationDays;

   const now = moment().utcOffset(1); // add UTC+1 for Berlin timezone
   const today = now.clone().day(); // will return day of the week as a number (Sunday = 0)
   const noon = now.clone().hour(12).minute(0).second(0);

   // add an additional day if the customer's current time is after 12:00 noon.
   if (now.isAfter(noon)) {
      daysToDisable = daysToDisable + 1;
   }

   // if today is Saturday or Sunday, add an additional day since grocery stores are closed.
   if (today === 6 || today === 0) {
      daysToDisable = daysToDisable + 1;
   }
   // if today is Friday, don't allow selecting weekend days.
   else if (today === 5) {
      daysToDisable = daysToDisable + 2;
   }

   return moment(new Date()).add(daysToDisable, 'days');
};

/**
 * The latest delivery date a customer can select for the menu they are viewing or have in
 * their cart.
 *
 * @return {Moment}
 */
export const maxDeliveryDate = () => {
   return moment(new Date()).add(2, 'year');
};


/**
 * Mixin for Vue components to use.
 *
 * @type {{computed: {datepickerLanguage(): *, datepickerDisabledDates(): {to: Date, from: Date}, datepickerInitialDate(): Date}, methods: {datepickerDateFormat(*=): string}}}
 */
export const DeliveryDateMixin = {
   computed: {
      /**
       * Dates that should be disabled on the date-picker field.
       *
       * @returns {{to: Date, from: Date}}
       */
      datepickerDisabledDates() {
         return {
            to: minDeliveryDate().toDate(),
            from: maxDeliveryDate().toDate(),
         }
      },

      /**
       * The date the date-picker field calendar opens on.
       *
       * @returns {Date}
       */
      datepickerInitialDate() {
         return moment(new Date()).add(3, 'days').toDate();
      },

      /**
       * The language the <datepicker> component should be in.
       *
       * @return {*}
       */
      datepickerLanguage() {
         switch (window.mymy.request.currentSite.language) {
            case 'en':
               return en;
            case 'nl':
               return nl;
            default:
               return de;
         }
      },
   },

   methods: {
      /**
       * Format the date in the date-picker field.
       *
       * @param date
       * @return {string}
       */
      datepickerDateFormat(date) {
         return moment(date).format(DATEPICKER_DATE_FORMAT);
      },

      /**
       * Format the 'delivery' field data into something that can be posted to Craft/Commerce.
       *
       * @param deliveryDate
       * @return {{datetime: string, timezone: (i.default.computed.fields.delivery.timezone|i.default.computed.fields.delivery.timezone|*)}}
       */
      getDeliveryDateForCraft(deliveryDate) {
         moment.tz.setDefault(deliveryDate.timezone)
         const [hh, ss] = deliveryDate.time ? deliveryDate.time.split(':') : [DEFAULT_DELIVERY_TIME_HOUR, DEFAULT_DELIVERY_TIME_MINUTE]
         return {
            timezone: deliveryDate.timezone,
            datetime: moment(deliveryDate.date)
               .hour(hh)
               .minute(ss)
               .second(DEFAULT_DELIVERY_TIME_SECOND)
               .format(),
         }
      },
   },
};